// Mensagens indicadas: sugestões de texto para cada momento do funil (por etapa e nicho).
// Usadas no chat (junto das mensagens rápidas) e no editor de automações.
// Os textos usam as variáveis das mensagens rápidas ({nome}, {meu_nome}, {empresa});
// nas automações, toAutomationBody() troca {nome} por {{lead_name}}.

export type SuggestionCategory =
    | 'primeiro_contato' | 'qualificacao' | 'agendamento' | 'proposta'
    | 'negociacao' | 'fechamento' | 'pos_venda' | 'reativacao';

export const CATEGORY_LABELS: Record<SuggestionCategory, string> = {
    primeiro_contato: 'Primeiro contato',
    qualificacao: 'Qualificação',
    agendamento: 'Agendamento',
    proposta: 'Proposta',
    negociacao: 'Negociação',
    fechamento: 'Fechamento',
    pos_venda: 'Pós-venda',
    reativacao: 'Reativação',
};

export interface MessageSuggestion {
    id: string;
    title: string;
    body: string;
    category: SuggestionCategory;
    /** Palavras do nome da etapa que tornam a sugestão indicada (sem acento, minúsculas). */
    keywords: string[];
    niches?: string[];
    /** Usa {meu_nome}/{empresa}: só faz sentido no chat, não em automação. */
    chatOnly?: boolean;
}

export const MESSAGE_SUGGESTIONS: MessageSuggestion[] = [
    {
        id: 'primeiro-contato', title: 'Primeiro contato', category: 'primeiro_contato',
        keywords: ['novo', 'new', 'entrada', 'prospec', 'contato', 'lead'],
        body: 'Olá, {nome}! Tudo bem? Vi que você demonstrou interesse e queria entender melhor o que você procura para te ajudar da melhor forma. Podemos conversar rapidinho?',
    },
    {
        id: 'boas-vindas', title: 'Recebemos seu contato', category: 'primeiro_contato',
        keywords: ['novo', 'new', 'entrada', 'recebid'],
        body: 'Olá, {nome}! Recebemos seu contato e já vamos te atender. Enquanto isso, pode me contar o que você está buscando?',
    },
    {
        id: 'apresentacao-atendente', title: 'Apresentação do atendente', category: 'primeiro_contato',
        keywords: ['novo', 'new', 'contato', 'atendimento'], chatOnly: true,
        body: 'Oi, {nome}, aqui é {meu_nome} da {empresa}. Posso te ajudar?',
    },
    {
        id: 'qualificacao', title: 'Entender a necessidade', category: 'qualificacao',
        keywords: ['qualifica', 'diagnost', 'descoberta', 'conversa', 'interesse'],
        body: '{nome}, para te passar a melhor opção, me conta: qual é o seu principal objetivo e para quando você precisa?',
    },
    {
        id: 'agendar', title: 'Propor horário', category: 'agendamento',
        keywords: ['agend', 'reuni', 'visita', 'apresenta', 'demonstra'],
        body: '{nome}, vamos agendar? Tenho horários disponíveis nesta semana. Qual período fica melhor para você, manhã ou tarde?',
    },
    {
        id: 'confirmar-agenda', title: 'Confirmar compromisso', category: 'agendamento',
        keywords: ['agendad', 'confirm', 'reuniao marcada', 'visita marcada'],
        body: 'Oi, {nome}! Passando para confirmar nosso compromisso. Está tudo certo para o horário combinado? Se precisar remarcar, é só me avisar.',
    },
    {
        id: 'proposta-enviada', title: 'Proposta enviada', category: 'proposta',
        keywords: ['proposta', 'orcamento', 'cotac'],
        body: '{nome}, enviei a proposta. Conseguiu dar uma olhada? Fico à disposição para tirar qualquer dúvida e ajustar o que for preciso.',
    },
    {
        id: 'followup-proposta', title: 'Follow-up da proposta', category: 'negociacao',
        keywords: ['proposta', 'negocia', 'follow', 'retorno', 'acompanha', 'analise'],
        body: 'Oi, {nome}! Passando para saber o que achou da proposta. Posso ajudar com algo para facilitar a sua decisão?',
    },
    {
        id: 'objecao-preco', title: 'Contornar objeção de preço', category: 'negociacao',
        keywords: ['negocia', 'objec', 'preco', 'valor', 'desconto'],
        body: 'Entendo, {nome}. Posso ver com você uma condição que caiba melhor no seu orçamento. O que seria ideal para você?',
    },
    {
        id: 'fechamento', title: 'Formalizar o fechamento', category: 'fechamento',
        keywords: ['fechament', 'ganho', 'fechad', 'contrato', 'pagamento', 'assinatura'],
        body: 'Perfeito, {nome}! Vou preparar tudo para formalizarmos. Pode me confirmar seus dados para o cadastro?',
    },
    {
        id: 'pos-venda', title: 'Está tudo certo?', category: 'pos_venda',
        keywords: ['pos', 'cliente', 'entrega', 'producao', 'onboarding', 'instala', 'acompanhamento'],
        body: 'Oi, {nome}! Passando para saber se está tudo certo. Se precisar de qualquer coisa, estou por aqui.',
    },
    {
        id: 'avaliacao-indicacao', title: 'Pedir avaliação e indicação', category: 'pos_venda',
        keywords: ['pos', 'cliente', 'concluid', 'finalizad', 'entregue'],
        body: '{nome}, ficamos felizes em ter você com a gente! Se puder, nos conte como foi a sua experiência. E se conhecer alguém que possa precisar, ficaremos gratos pela indicação.',
    },
    {
        id: 'reativacao', title: 'Retomar o contato', category: 'reativacao',
        keywords: ['perdid', 'frio', 'inativ', 'reativ', 'sem resposta', 'sem contato', 'parado'],
        body: 'Oi, {nome}! Faz um tempo que não nos falamos. Ainda faz sentido conversarmos sobre isso? Se mudou alguma coisa, me conta que eu ajudo.',
    },
    {
        id: 'cursos-matricula', title: 'Vagas da próxima turma', category: 'fechamento', niches: ['cursos'],
        keywords: ['matric', 'inscri', 'interessad', 'turma', 'vaga'],
        body: '{nome}, as vagas da próxima turma estão acabando. Quer que eu te explique como funciona a matrícula e as condições?',
    },
    {
        id: 'clinica-avaliacao', title: 'Agendar avaliação', category: 'agendamento', niches: ['clinica', 'estetica'],
        keywords: ['consulta', 'agend', 'avalia', 'procedimento'],
        body: 'Olá, {nome}! Podemos agendar a sua avaliação? Será um prazer te receber. Qual dia e horário ficam melhores para você?',
    },
    {
        id: 'acm-medidas', title: 'Pedir medidas para orçamento', category: 'qualificacao', niches: ['acm'],
        keywords: ['medi', 'orcamento', 'projeto', 'porta'],
        body: '{nome}, para fazermos o orçamento das portas, pode me enviar as medidas aproximadas dos vãos ou fotos do local? Se preferir, agendamos uma visita para medir.',
    },
    {
        id: 'fora-horario', title: 'Fora do horário', category: 'primeiro_contato',
        keywords: ['fora do horario'],
        body: 'Oi, {nome}! Recebi sua mensagem fora do horário de atendimento. Amanhã cedo eu te respondo, combinado?',
    },
];

export const normalizeText = (v: string | null | undefined) =>
    (v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const fitsNiche = (s: MessageSuggestion, niche: string | null | undefined) => !s.niches || s.niches.includes(normalizeText(niche));

/** Sugestões indicadas para uma etapa: as que combinam com o nome dela primeiro; sem combinação, as de primeiro contato. */
export function suggestionsForStage(
    stageName: string | null | undefined,
    niche: string | null | undefined,
    options: { limit?: number; forAutomation?: boolean } = {},
): MessageSuggestion[] {
    const { limit = 4, forAutomation = false } = options;
    const stage = normalizeText(stageName);
    const pool = MESSAGE_SUGGESTIONS.filter(s => fitsNiche(s, niche) && !(forAutomation && s.chatOnly));

    const scored = pool
        .map(s => ({
            s,
            score: stage
                ? s.keywords.reduce((n, k) => n + (stage.includes(k) ? 10 : 0), 0) + (s.niches && stage ? 3 : 0)
                : 0,
        }))
        .filter(x => x.score >= 10)
        .sort((a, b) => b.score - a.score)
        .map(x => x.s);

    const result = scored.length > 0 ? scored : pool.filter(s => s.category === 'primeiro_contato');
    return result.slice(0, limit);
}

/** Todas as sugestões do nicho, agrupadas por categoria (para a biblioteca completa). */
export function suggestionsByCategory(niche: string | null | undefined, forAutomation = false): { category: SuggestionCategory; items: MessageSuggestion[] }[] {
    const pool = MESSAGE_SUGGESTIONS.filter(s => fitsNiche(s, niche) && !(forAutomation && s.chatOnly));
    return (Object.keys(CATEGORY_LABELS) as SuggestionCategory[])
        .map(category => ({ category, items: pool.filter(s => s.category === category) }))
        .filter(g => g.items.length > 0);
}

/** Troca as variáveis do chat pelas das automações. */
export function toAutomationBody(body: string): string {
    return body.replace(/\{nome\}/gi, '{{lead_name}}');
}
